import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * iter63 — Shared branding fetcher.
 *
 * Both /login and /inquiry are pre-auth surfaces, so they hit
 * `GET /api/public/branding` (no auth) to resolve the cover image the
 * studio owner has configured under Admin → Settings.
 *
 * A module-level cache prevents a network hit on every remount, so tabbing
 * between /login and /inquiry stays instant. The cache is invalidated via
 * the tiny `bumpBrandingCache()` helper — called by the Settings page after
 * a successful save so the change is reflected without a hard reload.
 */

const DEFAULT_COVER =
  "https://customer-assets-4nw71qhi.emergentagent.net/" +
  "job_how-i-do-before/artifacts/roc6cvty_A%26R-76.webp";

let _brandingCache = null;    // last successful response
let _brandingPromise = null;  // in-flight fetch shared across callers

export function bumpBrandingCache(next) {
  _brandingCache = next ?? null;
  _brandingPromise = null;
}

async function fetchBranding() {
  const { data } = await axios.get(`${API}/public/branding`);
  // Stamp `source` so the useBranding effect gate stays stable across
  // renders (server response never carries a source field — without this
  // the setState below would trip the effect once more than needed).
  _brandingCache = { ...data, source: "api" };
  return _brandingCache;
}

export function resolveCoverFromQuery() {
  // Still honour ?cover=<url> for on-the-fly overrides during on-site
  // portfolio review sessions with the studio owner — matches the pre-iter63
  // Login page contract so existing bookmarks keep working.
  try {
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("cover") || "").trim();
    return q || null;
  } catch {
    return null;
  }
}

export function useBranding() {
  const [state, setState] = useState(() => {
    const q = resolveCoverFromQuery();
    if (q) return { cover_image_url: q, is_default: false, source: "query" };
    return _brandingCache || { cover_image_url: DEFAULT_COVER, is_default: true, source: "default" };
  });

  useEffect(() => {
    if (state.source === "query") return; // don't clobber URL overrides
    if (!_brandingPromise) _brandingPromise = fetchBranding().catch(() => null);
    _brandingPromise.then((data) => { if (data) setState(data); });
  }, [state.source]);

  return state;
}

export { DEFAULT_COVER as DEFAULT_COVER_IMAGE_URL };
